"use client";

/* eslint-disable @faire/ssr-no-restricted-globals */
import { createMemoryHistory, MemoryHistory } from "history";
import { useServerInsertedHTML } from "next/navigation";
import React, { useRef } from "react";
import { SSRRequestLocalStorage } from "./SSRRequestLocalStorage";

import { init as initializeWebApi } from "@faire/retailer/initialize/web-api";

import { scopedSingletonGetter } from "./scopedSingletonGetter";

export const Globals = ({
  children,
  globals,
  memoryHistory,
  userAgent,
  referer,
  originalUrl,
}: {
  children: React.ReactNode;
  globals: Record<string, unknown>;
  memoryHistory: MemoryHistory<unknown>;
  userAgent: string | null;
  referer: string | null;
  originalUrl: string;
}) => {
  const didInit = useRef(false);

  const didInsert = useRef(false);
  // TODO(jocelyn): This is a really inefficient way of adding a global variable, but we need this
  // at the very top, before @faire/web is initialized. We should remove this once we've removed
  // top-level use of LocalizationStore.
  //
  // eslint-disable-next-line @faire/no-inline-hoc-fc -- false positive
  useServerInsertedHTML(() => {
    if (didInsert.current) {
      return null;
    }
    didInsert.current = true;
    return (
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `var localeKey = ${JSON.stringify(globals.localeKey ?? "")}`,
        }}
      />
    );
  });

  if (typeof window === "undefined") {
    // Use the location according to the Faire backend, not according to Next.js.
    // Some differences: Next.js always includes a URL and doesn't ever end in a slash.
    const url = new URL(originalUrl);
    const reactRouterLocation = createMemoryHistory({
      initialEntries: [`${url.pathname}${url.search}`],
    }).location;
    const location: Location = {
      href: url.href,
      hostname: url.hostname,
      host: url.host,
      origin: url.origin,
      port: url.port,
      protocol: url.protocol,
      ancestorOrigins: [] as never as DOMStringList,
      assign() { },
      reload() { },
      replace() { },
      ...reactRouterLocation,
    };

    // Note that since this is rendered on the server, this will not be
    // rerendered. Recreating the globals on the client every render wouldn't
    // necessarily be safe.
    return (
      <SSRRequestLocalStorage.Provider
        value={{
          global: {
            ...globals,
            isNextJS: true,
            location,
            memoryHistory,
            isSSR: true,
            window: {
              location,
              navigator: {
                userAgent,
              },
              document: {
                referrer: referer ?? "",
              },
              addEventListener: () => { },
            },
            faire: {
              singletons: {
                getter: scopedSingletonGetter,
              },
              api: {
                // TODO(jocelyn): We probably don't actually want to silently resolve fetches,
                // but we currently assume that fetches work (e.g., when assigning).
                // Investigate and consider removing.
                handler: () => Promise.resolve(),
              },
              app: "web-retailer",
            },
          },
        }}
      >
        {children}
      </SSRRequestLocalStorage.Provider>
    );
  } else {
    // This isn't an effect, because we need this to run before any other
    // code. (Please don't copy this code into other files, this is a hack.)
    if (!didInit.current) {
      for (const [key, value] of Object.entries(globals)) {
        // @ts-expect-error -- These are globals (we want to get rid of globals).
        // Note that "faire" is initialized earlier, in `indexHead.ts`.
        if (key !== "faire" || !window[key]) {
          // @ts-expect-error -- These are globals (we want to get rid of globals).
          window[key] = value;
        }
      }
      didInit.current = true;

      // @ts-expect-error -- These are globals (we want to get rid of globals).
      window["isNextJS"] = true;

      // @ts-expect-error -- These are globals (we want to get rid of globals).
      window["cache"] = {};

      initializeWebApi();
      didInit.current = true;
    }

    return <>{children}</>;
  }
};
